/**
 * How a coaching partnership's status is shown to an admin.
 *
 * The stored values are the coachee's own vocabulary, and one of them reads
 * badly out of context: `chemistry` means the coachee has **shortlisted** this
 * coach - it is set by the "Add to shortlist" button - not that a chemistry
 * session exists. Rendered raw next to a coachee's sessions, it is naturally
 * read as a session type, so a coachee who shortlisted three coaches looks like
 * one who used three chemistry calls.
 *
 * The status is right; only the word was wrong. Do not "fix" this by moving a
 * partnership off `chemistry` when a chemistry session is cancelled: the coach
 * is still shortlisted, the coachee can rebook, and `chemistry` is also what
 * grants that coach sight of the coachee's goals - a merely `pending`
 * partnership grants nothing.
 *
 * @module utils/coaching/partnership-status
 */

const STATUS_CLASS: Record<string, string> = {
  pending: 'text-bg-secondary',
  chemistry: 'text-bg-info',
  declined: 'text-bg-warning',
  chosen: 'text-bg-success',
  reassigned: 'text-bg-secondary',
};

/**
 * Lowercase to match the outcome badges rendered beside these - "completed",
 * "upcoming", "cancelled" - and the casing the raw statuses already had.
 */
const STATUS_LABEL: Record<string, string> = {
  pending: 'matched',
  chemistry: 'shortlisted',
  declined: 'declined',
  chosen: 'chosen',
  reassigned: 'reassigned',
};

/** Bootstrap badge class for a partnership status. */
export function partnershipStatusClass(status: string | undefined): string {
  return STATUS_CLASS[status ?? ''] ?? 'text-bg-secondary';
}

/**
 * Human label for a partnership status.
 *
 * Falls back to the raw value rather than to "unknown", so a status added to
 * the API before this map is updated still shows something true.
 */
export function partnershipStatusLabel(status: string | undefined): string {
  if (!status) {
    return 'unknown';
  }

  return STATUS_LABEL[status] ?? status;
}
